import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import DefaultVolunteerImage from './DefaultImage';
import { Sidebar, Logo } from '../Shared/Sidebar';

import { isPortrait } from '../../utils/size';
import { MonthDropdownValues } from '../../utils/enums';
import { copyFor } from '../../copy/copy';

const SidebarContainer = styled(Sidebar)`
    display: flex;
    flex-direction: ${() => (isPortrait() ? 'row-reverse' : 'column')};
    align-items: flex-start;
    > svg {
        max-height: 40px;
        width: 40px;
    }
`;

const OrganizationLogo = styled(Logo)`
    max-width: 120px;
    max-height: 70px;
    margin: 0;
    object-fit: scale-down;
`;

const DetailsContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-top: 5px;
`;

const DateTimeContainer = styled.div`
    margin-bottom: 5px;
    p {
        font-size: 11px;
        font-weight: 600;
        margin: 2px 0;
    }
`;

const Detail = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.33;
    font-weight: 400;

    &:last-of-type {
        margin-bottom: ${() => (isPortrait() ? 0 : 5)}px;
    }
`;

const MapLink = styled.a`
    font-size: 9px;
    font-weight: 600;
    color: #ff245e;
    margin: 0;
    margin-bottom: 5px;
    text-decoration: none;
    text-transform: uppercase;
`;

const SkillsContainer = styled.div`
    margin: 5px 0;
`;

const SkillsHeader = styled.p`
    font-size: 10px;
    margin: 0;
    font-weight: 800;
    font-family: Averta;
    text-transform: uppercase;
`;
const Skills = styled.p`
    margin: 3px 0;
    font-size: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const buildMapsUrl = (address1 = '', address2 = '', city = '', state = '', zip = '') => {
    const baseURL = `https://www.google.com/maps/search/?api=1&query=`;
    const paramsString = [address1, address2, city, state, zip]
        .join(' ')
        .replace(/ /g, '+');
    return baseURL + paramsString;
};

const buildDateRange = ({ startDate, endDate, singleDayOpportunity, onGoing }) => {
    if (onGoing) return copyFor('opportunity', 'ongoing', 'volunteer');
    if (startDate === undefined || endDate === undefined)
        return copyFor('opportunity', 'flexibleDates', 'volunteer');

    const s = new Date(startDate);
    const start = {
        day: s.getUTCDate(),
        month: MonthDropdownValues[s.getMonth()],
        year: s.getUTCFullYear()
    };

    if (singleDayOpportunity) {
        return `${start.month} ${start.day}, ${start.year}`;
    } else {
        const e = new Date(endDate);
        const end = {
            day: e.getUTCDate(),
            month: MonthDropdownValues[e.getMonth()],
            year: e.getUTCFullYear()
        };
        return `${start.month} ${start.day}, ${start.year} - ${end.month} ${end.day}, ${
            end.year
        }`;
    }
};

const buildTimeRange = ({ startDate, endDate }) => {
    if (startDate === undefined || endDate === undefined) return;
    if (startDate === endDate) return '';
    const startDateUTC = new Date(startDate);
    const endDateUTC = new Date(endDate);

    let startDT = new Date();
    startDT.setHours(startDateUTC.getUTCHours());
    startDT.setMinutes(startDateUTC.getUTCMinutes());

    let endDT = new Date();
    endDT.setHours(endDateUTC.getUTCHours());
    endDT.setMinutes(endDateUTC.getUTCMinutes());

    const s = new Date(startDT).toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: 'true'
    });

    const e = new Date(endDT).toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: 'true'
    });

    return `${s} - ${e}`;
};

const VolunteerSidebar = props => {
    const v = props.volunteerOpportunity;
    return (
        <SidebarContainer hasImage={v.imageUrl || false}>
            {!isPortrait() &&
                (v.imageUrl ? (
                    <OrganizationLogo
                        src={decodeURIComponent(v.imageUrl).replace(/https/g, 'http')}
                    />
                ) : (
                    <DefaultVolunteerImage issueId={props.associatedOpportunityIssueId} />
                ))}
            <DetailsContainer hasImage={v.imageUrl}>
                <DateTimeContainer>
                    <Detail>{buildDateRange(v.schedule)}</Detail>
                    <Detail>{buildTimeRange(v.schedule)}</Detail>
                </DateTimeContainer>
                <Detail>{v.address.street1 || ''}</Detail>
                <Detail>
                    {`${v.address.street2 || ''} ${
                        v.address.city ? v.address.city + ',' : ''
                    } ${v.address.state} ${v.address.zipCode || ''}`}{' '}
                </Detail>
                <MapLink
                    href={buildMapsUrl(
                        v.address.street1,
                        v.address.street2,
                        v.address.city,
                        v.address.state,
                        v.address.zipCode
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {copyFor('opportunity', 'map', 'volunteer')}
                </MapLink>
                {v.skillsList.length > 0 && (
                    <SkillsContainer>
                        <SkillsHeader>
                            {copyFor('opportunity', 'skills', 'volunteer')}
                        </SkillsHeader>
                        {isPortrait() ? (
                            <Skills>
                                {v.skillsList
                                    .slice(0, 3)
                                    .map((s, i) => (i ? ', ' : '') + s)}
                            </Skills>
                        ) : (
                            v.skillsList
                                .slice(0, 3)
                                .map((s, i) => <Skills key={i}>{s}</Skills>)
                        )}
                    </SkillsContainer>
                )}
            </DetailsContainer>
        </SidebarContainer>
    );
};

VolunteerSidebar.propTypes = {
    volunteerOpportunity: PropTypes.object.isRequired,
    associatedOpportunityIssueId: PropTypes.number
};

export default connect(
    state => {
        return {
            associatedOpportunityIssueId: state.widget.activeAction.issue.id,
            isMobileResolution: state.widget.isMobileResolution,
            showReceiveUpdates: true
        };
    },
    {
        isPortrait
    }
)(VolunteerSidebar);



// WEBPACK FOOTER //
// ./src/components/Volunteer/VolunteerSidebar.jsx